import React from 'react';
import { Form, FormInstance, Tooltip, InputNumber } from 'antd';
import { NumberFieldContainer } from './CompressorInputComponents.styles';
import type { NamePath } from 'antd/es/form/interface';
import { ErrorIcon } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/RoomConfigurationContent.styles';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'antd/es/form/Form';
import { Room } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/RoomConfigurationContent.utils';
import { COMPRESSOR_DATA, ROOMS } from '@/store/SiteSurveyForm/constants';

interface CompressorNumberInputProps {
  index: number;
  pressureType: string;
  roomIndex: number;
  form: FormInstance;
  initialValue?: number;
  compressorId: string;
  roomId: number;
  isCompressorNumberDuplicate: (
    rooms: Room[],
    roomIndex: number,
    index: number,
    number: number,
  ) => Record<number, Record<number, number>>;
}

export const CompressorNumberInput: React.FC<CompressorNumberInputProps> = ({
  index,
  pressureType,
  roomIndex,
  form,
  initialValue,
  isCompressorNumberDuplicate,
}) => {
  const fieldName: NamePath = [pressureType, ROOMS, roomIndex, COMPRESSOR_DATA, index, 'number'];
  const roomsFieldName: NamePath = [pressureType, ROOMS];
  const fieldValue = useWatch(fieldName, form);
  const rooms = useWatch(roomsFieldName, form);
  const { t } = useTranslation();

  const validateCompressorNumber = async () => {
    const duplicates = isCompressorNumberDuplicate(rooms, roomIndex, index, fieldValue);
    const fieldsToUpdate = [];
    rooms.forEach((room, currentRoomIndex) => {
      (room?.compressorData || []).forEach((comp, currentCompIndex) => {
        const currentFieldName: NamePath = [
          pressureType,
          ROOMS,
          currentRoomIndex,
          COMPRESSOR_DATA,
          currentCompIndex,
          'number',
        ];
        if (duplicates?.[currentRoomIndex]?.[currentCompIndex]) {
          fieldsToUpdate.push({ name: currentFieldName, errors: [t('This number is already in use')] });
        } else {
          fieldsToUpdate.push({ name: currentFieldName, errors: [] });
        }
      });
    });

    form.setFields(fieldsToUpdate);

    if (duplicates?.[roomIndex]?.[index]) {
      return Promise.reject(t('This number is already in use'));
    } else {
      return Promise.resolve();
    }
  };

  return (
    <NumberFieldContainer>
      <Form.Item
        name={fieldName}
        className="compressor-number-input"
        initialValue={initialValue}
        preserve={true}
        help=""
        validateStatus={
          !fieldValue || isCompressorNumberDuplicate(rooms, roomIndex, index, fieldValue)?.[roomIndex]?.[index]
            ? 'error'
            : ''
        }
        rules={[
          {
            required: true,
            message: t('Compressor number is required'),
          },
          { message: t('This number is already in use'), validator: validateCompressorNumber },
        ]}
        dependencies={[[pressureType, ROOMS]]}
      >
        <InputNumber min={1} className="compressor-number-input" placeholder="" />
      </Form.Item>
      {isCompressorNumberDuplicate(rooms, roomIndex, index, fieldValue)?.[roomIndex]?.[index] && (
        <Tooltip title={t('This number is already in use')}>
          <ErrorIcon />
        </Tooltip>
      )}
      {!fieldValue && (
        <Tooltip title={t('Compressor number is required')}>
          <ErrorIcon />
        </Tooltip>
      )}
    </NumberFieldContainer>
  );
};
